
export interface ErrorContext {
  feature?: string;
  used?: number;
  limit?: number;
  currentPlan?: string;
  action?: string;
  resourceType?: 'session' | 'cv' | 'analysis' | 'feature';
}

export interface UserFriendlyError {
  title: string;
  message: string;
  actionText?: string;
  actionUrl?: string;
  severity: 'error' | 'warn' | 'info';
  showUpgrade?: boolean;
}

/**
 * Convert API errors and status codes to user-friendly messages
 */
export const getUserFriendlyError = (
  error: any,
  context: ErrorContext = {}
): UserFriendlyError => {

  // Handle specific error codes first
  if (error?.response?.data?.error) {
    const errorCode = error.response.data.error;
    const errorData = error.response.data;

    switch (errorCode) {
      case 'USAGE_LIMIT_EXCEEDED':
        return getUsageLimitError(errorData, context);

      case 'PLAN_LIMIT_EXCEEDED_CREDITS_AVAILABLE':
        return getCreditAvailableError(errorData, context);

      case 'USAGE_LIMIT_EXCEEDED_NO_CREDITS':
        return getNoCreditsError(errorData, context);

      case 'PLAN_LIMIT_EXCEEDED_ALLOWANCES_AVAILABLE':
        return getAllowancesAvailableError(errorData, context);

      case 'FEATURE_ACCESS_DENIED':
        return getFeatureAccessError(errorData, context);

      case 'EXPORT_FORMAT_DENIED':
        return getExportFormatError(errorData, context);

      case 'UNAUTHORIZED':
        return {
          title: 'Authentication Required',
          message: 'Please log in to continue using this feature.',
          actionText: 'Log In',
          actionUrl: '/login',
          severity: 'warn',
          showUpgrade: false
        };
    }
  }

  // Handle HTTP status codes
  const status = error?.response?.status || error?.status;

  switch (status) {
    case 401:
      return {
        title: 'Session Expired',
        message: 'Your session has expired. Please log in again to continue.',
        actionText: 'Log In',
        actionUrl: '/login',
        severity: 'warn',
        showUpgrade: false
      };

    case 403:
      return {
        title: 'Access Restricted',
        message: 'This feature is not available on your current plan. Upgrade to unlock more capabilities!',
        actionText: 'View Plans',
        actionUrl: '/pricing',
        severity: 'warn',
        showUpgrade: true
      };

    case 429:
      return getUsageLimitError(error?.response?.data, context);

    case 404:
      return {
        title: 'Not Found',
        message: `The ${context.resourceType || 'resource'} you're looking for doesn't exist or may have been deleted.`,
        severity: 'error',
        showUpgrade: false
      };

    case 500:
    case 502:
    case 503:
      return {
        title: 'Service Temporarily Unavailable',
        message: 'We\'re experiencing technical difficulties. Please try again in a few minutes.',
        actionText: 'Try Again',
        severity: 'error',
        showUpgrade: false
      };

    default:
      return getGenericError(error, context);
  }
};

/**
 * Handle usage limit exceeded errors
 */
const getUsageLimitError = (errorData: any, context: ErrorContext): UserFriendlyError => {
  const usageInfo = errorData?.usage || {};
  const usageType = usageInfo.type || context.resourceType || 'feature';

  const messages = {
    job_analysis: {
      title: '🔍 Job Analysis Limit Reached',
      message: `You've used up all your job description analyses this month. Upgrade your plan or buy more credits to analyze more positions and advance your career faster!`,
      actionText: 'Buy More Credits'
    },
    cv_generation: {
      title: '📄 CV Generation Limit Reached',
      message: `You've used up all your tailored CVs this month. Upgrade your plan or buy more credits to generate more CVs and maximize your job applications!`,
      actionText: 'Buy More Credits'
    },
    session: {
      title: '🎯 Session Limit Reached',
      message: `You've used up all your interview sessions this month. Upgrade your plan or buy more credits to practice more times per session and continue improving your interview skills!`,
      actionText: 'Buy More Credits'
    },
    cv_storage: {
      title: '💾 CV Storage Full',
      message: `You've stored up all your CVs generation allowances. Upgrade your plan or buy more credits to store more CVs!`,
      actionText: 'Buy More Credits'
    },
    practice_attempt: {
      title: '🔄 Practice Limit Reached',
      message: `You've used up all your practice attempts for this session. Upgrade your plan or buy more credits to practice more times per session!`,
      actionText: 'Buy More Credits'
    },
    interview_session: {
      title: '🎯 Session Limit Reached',
      message: `You've used up all your interview sessions this month. Upgrade your plan or buy more credits to practice more times per session and continue improving your interview skills!`,
      actionText: 'Buy More Credits'
    }
  };

  const errorConfig = messages[usageType as keyof typeof messages] || {
    title: '⚡ Usage Limit Reached',
    message: `You've reached your monthly limit. Upgrade your plan or buy more credits to continue using this feature!`,
    actionText: 'Continue'
  };

  return {
    ...errorConfig,
    actionUrl: '/dashboard/credits/allowances',
    severity: 'warn',
    showUpgrade: true
  };
};

/**
 * Handle feature access denied errors
 */
const getFeatureAccessError = (errorData: any, context: ErrorContext): UserFriendlyError => {
  const feature = context.feature || 'feature';
  console.error(errorData);

  const featureMessages = {
    'company-research': {
      title: '🏢 Company Research Premium Feature',
      message: 'Get detailed insights about companies, their culture, and interview processes. Available on Basic plans and above!',
    },
    'skills-gap-analysis': {
      title: '📊 Advanced Skills Analysis',
      message: 'Discover exactly which skills to develop for your dream job. Unlock detailed gap analysis with Pro plans!',
    },
    'ats-optimization': {
      title: '🤖 ATS Optimization',
      message: 'Beat applicant tracking systems with AI-powered resume optimization. Available on Pro plans and above!',
    },
    'advanced-export': {
      title: '📤 Advanced Export Formats',
      message: 'Export your CVs in multiple professional formats. Upgrade to access Word, HTML, and custom formats!',
    }
  };

  const errorConfig = featureMessages[feature as keyof typeof featureMessages] || {
    title: '🔒 Premium Feature',
    message: `${feature} is available on higher tier plans. Upgrade to unlock advanced career tools!`,
  };

  return {
    ...errorConfig,
    actionText: 'View Plans',
    actionUrl: '/pricing',
    severity: 'info',
    showUpgrade: true
  };
};

/**
 * Handle export format denied errors
 */
const getExportFormatError = (errorData: any, context: ErrorContext): UserFriendlyError => {
  const format = context.feature || 'format';
  console.error(errorData);

  return {
    title: `📄 ${format.toUpperCase()} Export Unavailable`,
    message: `${format.toUpperCase()} export is a premium feature. Upgrade your plan to download CVs in multiple professional formats!`,
    actionText: 'Upgrade for Export Options',
    actionUrl: '/pricing',
    severity: 'info',
    showUpgrade: true
  };
};

/**
 * Handle generic errors
 */
const getGenericError = (error: any, context: ErrorContext): UserFriendlyError => {
  // Try to extract message from error response
  console.error(context);
  const message = error?.response?.data?.message ||
    error?.response?.data?.error?.message ||
    error?.message;

  if (message && typeof message === 'string') {
    return {
      title: 'Something Went Wrong',
      message: message,
      severity: 'error',
      showUpgrade: false
    };
  }

  // Network errors
  if (error?.code === 'NETWORK_ERROR' || !navigator.onLine) {
    return {
      title: '🌐 Connection Issue',
      message: 'Please check your internet connection and try again.',
      actionText: 'Retry',
      severity: 'error',
      showUpgrade: false
    };
  }

  // Default fallback
  return {
    title: 'Unexpected Error',
    message: 'Something unexpected happened. Please try again or contact support if the problem persists.',
    actionText: 'Contact Support',
    actionUrl: 'mailto:support@prepbestie.com',
    severity: 'error',
    showUpgrade: false
  };
};

/**
 * Get upgrade message based on current plan and target feature
 */
export const getUpgradeMessage = (
  currentPlan: string = 'Free',
): string => {
  const planUpgrades = {
    'Free': 'Upgrade to Basic ($9.99/month) to unlock this feature!',
    'Basic': 'Upgrade to Pro ($19.99/month) for advanced features!',
    'Pro': 'Upgrade to Enterprise for unlimited access!',
  };

  return planUpgrades[currentPlan as keyof typeof planUpgrades] ||
    'Upgrade your plan to access this feature!';
};

/**
 * Handle credit available errors
 */
const getCreditAvailableError = (errorData: any, context: ErrorContext): UserFriendlyError => {
  const creditCost = errorData?.creditCost || 0;
  const remainingCredits = errorData?.remainingCredits || 0;
  const feature = context.feature || 'feature';
  const hasAllowanceUrl = errorData?.allowanceUrl;

  return {
    title: '💳 Use Credits or Buy Allowances',
    message: `You've reached your plan limit, but you can continue using ${feature} with ${creditCost} credits or buy allowances for future use. You have ${remainingCredits} credits remaining.`,
    actionText: hasAllowanceUrl ? 'Buy Allowances' : 'Use Credits',
    actionUrl: hasAllowanceUrl || '/dashboard/credits',
    severity: 'warn',
    showUpgrade: false
  };
};

/**
 * Handle no credits available errors
 */
const getNoCreditsError = (errorData: any, context: ErrorContext): UserFriendlyError => {
  const creditCost = errorData?.creditCost || 0;
  const remainingCredits = errorData?.remainingCredits || 0;
  const feature = context.feature || 'feature';

  return {
    title: '💳 Insufficient Credits',
    message: `You've reached your plan limit and don't have enough credits. You need ${creditCost} credits to use ${feature}, but only have ${remainingCredits} credits.`,
    actionText: 'Buy More Credits',
    actionUrl: '/dashboard/credits',
    severity: 'warn',
    showUpgrade: true
  };
};

/**
 * Handle allowances available errors
 */
const getAllowancesAvailableError = (errorData: any, context: ErrorContext): UserFriendlyError => {
  const feature = context.feature || 'feature';
  const creditCost = errorData?.creditCost || 0;

  return {
    title: '🎯 Use Allowances or Credits',
    message: `You've reached your plan limit for ${feature}. You can use your purchased allowances or buy more with ${creditCost} credits.`,
    actionText: 'Manage Allowances',
    actionUrl: '/dashboard/credits/allowances',
    severity: 'warn',
    showUpgrade: false
  };
};

/**
 * Get contextual help text based on the error type
 */
export const getHelpText = (errorType: string): string => {
  const helpTexts = {
    'usage_limit': 'Usage limits reset monthly. Upgrade anytime to increase your limits immediately.',
    'feature_access': 'Premium features help you stand out in the job market with advanced AI insights.',
    'export_format': 'Different employers prefer different CV formats. Premium plans support all major formats.',
    'storage': 'Keep multiple versions of your CV for different job applications and industries.',
    'credits': 'Credits let you use features beyond your plan limits. Purchase credits anytime to continue using premium features.'
  };

  return helpTexts[errorType as keyof typeof helpTexts] ||
    'Contact our support team if you need assistance with your account.';
};
